import { useState, useEffect } from 'react';
import { Plus, X, MagnifyingGlass, PencilSimple } from '@phosphor-icons/react';
import api from '../api';

const INP = {
  background: 'hsl(0,0%,7%)', border: '1px solid hsl(0,0%,18%)',
  padding: '9px 12px', color: 'hsl(0,0%,90%)',
  fontFamily: "'JetBrains Mono', monospace", fontSize: 12,
  outline: 'none', width: '100%', boxSizing: 'border-box',
};
const LBL = {
  fontFamily: "'JetBrains Mono', monospace", fontSize: 9, color: 'hsl(0,0%,45%)',
  textTransform: 'uppercase', letterSpacing: '0.12em', marginBottom: 4, display: 'block',
};
const SECTION = {
  fontFamily: "'JetBrains Mono', monospace", fontSize: 9, color: 'hsl(0,0%,35%)',
  textTransform: 'uppercase', letterSpacing: '0.15em',
  marginBottom: 10, borderBottom: '1px solid hsl(0,0%,12%)', paddingBottom: 6,
};

const GTA_OPTIONS = [
  { value: 'forward_12', label: 'Forward Charge — 12%' },
  { value: 'reverse_5', label: 'Reverse Charge — 5%' },
];

const EMPTY = {
  name: '', code: '', gstin: '', pan: '',
  city: '', state: '', pincode: '',
  contact_name: '', contact_phone: '', contact_email: '',
  bank_account_no: '', bank_ifsc: '', bank_name: '',
  payment_terms: 'Net 30',
  transporter_id: '',
  gta_gst_type: 'forward_12',
  service_areas: '',
  vehicle_types: '',
};

function CarrierModal({ carrier, onClose, onSaved }) {
  const isEdit = !!carrier?.id;
  const [form, setForm] = useState(isEdit ? {
    name: carrier.name || '',
    code: carrier.code || '',
    gstin: carrier.gstin || '',
    pan: carrier.pan || '',
    city: carrier.registered_address?.city || carrier.city || '',
    state: carrier.registered_address?.state || carrier.state || '',
    pincode: carrier.registered_address?.pincode || '',
    contact_name: carrier.contact_name || '',
    contact_phone: carrier.contact_phone || '',
    contact_email: carrier.contact_email || '',
    bank_account_no: carrier.bank_account_no || '',
    bank_ifsc: carrier.bank_ifsc || '',
    bank_name: carrier.bank_name || '',
    payment_terms: carrier.payment_terms || 'Net 30',
    transporter_id: carrier.transporter_id || '',
    gta_gst_type: carrier.gta_gst_type || 'forward_12',
    service_areas: (carrier.service_areas || []).join(', '),
    vehicle_types: (carrier.vehicle_types || []).join(', '),
  } : { ...EMPTY });
  const [saving, setSaving] = useState(false);
  const set = (k, v) => setForm(p => ({ ...p, [k]: v }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    const payload = {
      ...form,
      registered_address: { city: form.city, state: form.state, pincode: form.pincode },
      service_areas: form.service_areas ? form.service_areas.split(',').map(s => s.trim()).filter(Boolean) : [],
      vehicle_types: form.vehicle_types ? form.vehicle_types.split(',').map(s => s.trim()).filter(Boolean) : [],
    };
    try {
      if (isEdit) { await api.put(`/carriers/${carrier.id}`, payload); }
      else { await api.post('/carriers', payload); }
      onSaved();
    } catch (err) { console.error(err); }
    setSaving(false);
  };

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.85)', zIndex: 300,
      display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}>
      <div style={{ background: 'hsl(0,0%,5%)', border: '1px solid hsl(0,0%,20%)',
        width: '100%', maxWidth: 600, padding: 24, maxHeight: '92vh', overflowY: 'auto' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 20 }}>
          <div style={{ fontFamily: "'Clash Display', sans-serif", fontSize: 16,
            color: 'hsl(0,0%,98%)', textTransform: 'uppercase' }}>
            {isEdit ? 'Edit Carrier' : 'Add Carrier'}
          </div>
          <button onClick={onClose} style={{ background: 'none', border: 'none',
            cursor: 'pointer', color: 'hsl(0,0%,50%)' }}><X size={16} /></button>
        </div>
        <form onSubmit={handleSubmit}>
          <div style={{ marginBottom: 16 }}>
            <div style={SECTION}>Basic Info</div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
              <div><label style={LBL}>Carrier Name *</label>
                <input value={form.name} onChange={e => set('name', e.target.value)}
                  required style={INP} data-testid="carrier-name" /></div>
              <div><label style={LBL}>Code</label>
                <input value={form.code} onChange={e => set('code', e.target.value)}
                  placeholder="CAR-XXX" style={INP} /></div>
              <div><label style={LBL}>GSTIN</label>
                <input value={form.gstin} onChange={e => set('gstin', e.target.value)}
                  placeholder="22AAAAA0000A1Z5" style={INP} /></div>
              <div><label style={LBL}>PAN</label>
                <input value={form.pan} onChange={e => set('pan', e.target.value)}
                  placeholder="AAAAA0000A" style={INP} /></div>
            </div>
          </div>
          <div style={{ marginBottom: 16 }}>
            <div style={SECTION}>Logistics Details</div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 12 }}>
              <div><label style={LBL}>Transporter ID (NIC)</label>
                <input value={form.transporter_id} onChange={e => set('transporter_id', e.target.value)}
                  placeholder="e.g. 88AABCU9603R1ZM" style={INP} /></div>
              <div><label style={LBL}>GTA GST Type</label>
                <select value={form.gta_gst_type} onChange={e => set('gta_gst_type', e.target.value)}
                  style={{ ...INP, appearance: 'none' }}>
                  {GTA_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                </select>
              </div>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
              <div><label style={LBL}>Vehicle Types (comma-separated)</label>
                <input value={form.vehicle_types} onChange={e => set('vehicle_types', e.target.value)}
                  placeholder="Truck, Tempo, Container" style={INP} /></div>
              <div><label style={LBL}>Service Areas (comma-separated)</label>
                <input value={form.service_areas} onChange={e => set('service_areas', e.target.value)}
                  placeholder="Gujarat, Rajasthan, Maharashtra" style={INP} /></div>
            </div>
          </div>
          <div style={{ marginBottom: 16 }}>
            <div style={SECTION}>Address &amp; Contact</div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 120px', gap: 12, marginBottom: 12 }}>
              <div><label style={LBL}>City</label>
                <input value={form.city} onChange={e => set('city', e.target.value)} style={INP} /></div>
              <div><label style={LBL}>State</label>
                <input value={form.state} onChange={e => set('state', e.target.value)} style={INP} /></div>
              <div><label style={LBL}>Pincode</label>
                <input value={form.pincode} onChange={e => set('pincode', e.target.value)} style={INP} /></div>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 12 }}>
              <div><label style={LBL}>Contact Name</label>
                <input value={form.contact_name} onChange={e => set('contact_name', e.target.value)} style={INP} /></div>
              <div><label style={LBL}>Phone</label>
                <input value={form.contact_phone} onChange={e => set('contact_phone', e.target.value)} style={INP} /></div>
              <div><label style={LBL}>Email</label>
                <input type="email" value={form.contact_email} onChange={e => set('contact_email', e.target.value)} style={INP} /></div>
            </div>
          </div>
          <div style={{ marginBottom: 16 }}>
            <div style={SECTION}>Bank &amp; Payment</div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 12 }}>
              <div><label style={LBL}>Bank Name</label>
                <input value={form.bank_name} onChange={e => set('bank_name', e.target.value)} style={INP} /></div>
              <div><label style={LBL}>Payment Terms</label>
                <input value={form.payment_terms} onChange={e => set('payment_terms', e.target.value)}
                  placeholder="Net 30" style={INP} /></div>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
              <div><label style={LBL}>Account No</label>
                <input value={form.bank_account_no} onChange={e => set('bank_account_no', e.target.value)} style={INP} /></div>
              <div><label style={LBL}>IFSC</label>
                <input value={form.bank_ifsc} onChange={e => set('bank_ifsc', e.target.value)} style={INP} /></div>
            </div>
          </div>
          <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
            <button type="button" onClick={onClose} className="btn-ghost">Cancel</button>
            <button type="submit" disabled={saving} className="btn-primary" data-testid="save-carrier-btn">
              {saving ? 'Saving...' : isEdit ? 'Update Carrier' : 'Add Carrier'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default function CarriersPage() {
  const [carriers, setCarriers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [editCarrier, setEditCarrier] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get('/carriers', { params: { search } });
      setCarriers(r.data);
    } catch (err) { console.error(err); }
    setLoading(false);
  };
  useEffect(() => { load(); }, [search]); // eslint-disable-line react-hooks/exhaustive-deps

  const toggleStatus = async (c) => {
    await api.patch(`/carriers/${c.id}/status`, { status: c.status === 'active' ? 'inactive' : 'active' });
    load();
  };

  const gtaLabel = (val) => val === 'reverse_5' ? 'Rev. 5%' : 'Fwd. 12%';

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 20 }}>
        <div>
          <h1 style={{ fontFamily: "'Clash Display', sans-serif", fontSize: 22, fontWeight: 700,
            color: 'hsl(0,0%,98%)', textTransform: 'uppercase', letterSpacing: '0.08em', margin: 0 }}>
            Carriers
          </h1>
          <p style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 11, color: 'hsl(0,0%,40%)', marginTop: 4 }}>
            Logistics &amp; transport providers
          </p>
        </div>
        <button onClick={() => { setEditCarrier(null); setShowModal(true); }}
          className="btn-primary" data-testid="add-carrier-btn"
          style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 11 }}>
          <Plus size={14} /> Add Carrier
        </button>
      </div>

      <div style={{ position: 'relative', marginBottom: 16, maxWidth: 320 }}>
        <MagnifyingGlass size={14} style={{ position: 'absolute', left: 10, top: '50%',
          transform: 'translateY(-50%)', color: 'hsl(0,0%,40%)', pointerEvents: 'none' }} />
        <input value={search} onChange={e => setSearch(e.target.value)}
          placeholder="Search by name, code, GSTIN..."
          data-testid="carrier-search"
          style={{ ...INP, paddingLeft: 30 }} />
      </div>

      <div style={{ border: '1px solid hsl(0,0%,14%)' }}>
        <div style={{ display: 'grid', gridTemplateColumns: '200px 100px 160px 140px 100px 90px 110px',
          background: 'hsl(0,0%,8%)', padding: '8px 12px', borderBottom: '1px solid hsl(0,0%,14%)' }}>
          {['Name','Code','GSTIN','Contact','Transporter ID','GTA GST','Actions'].map(h => (
            <span key={h} style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 9,
              color: 'hsl(0,0%,40%)', textTransform: 'uppercase', letterSpacing: '0.1em' }}>{h}</span>
          ))}
        </div>
        {loading ? (
          <div style={{ padding: 32, textAlign: 'center', fontFamily: "'JetBrains Mono', monospace",
            fontSize: 12, color: 'hsl(0,0%,40%)' }}>Loading...</div>
        ) : carriers.length === 0 ? (
          <div style={{ padding: 32, textAlign: 'center', fontFamily: "'JetBrains Mono', monospace",
            fontSize: 12, color: 'hsl(0,0%,35%)' }}>No carriers found</div>
        ) : carriers.map(c => (
          <div key={c.id} data-testid="carrier-row"
            style={{ display: 'grid', gridTemplateColumns: '200px 100px 160px 140px 100px 90px 110px',
              padding: '10px 12px', borderBottom: '1px solid hsl(0,0%,10%)', alignItems: 'center' }}>
            <div>
              <div style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 12,
                color: c.status === 'inactive' ? 'hsl(0,0%,40%)' : 'hsl(0,0%,90%)' }}>{c.name}</div>
              <div style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 9,
                color: 'hsl(0,0%,35%)', marginTop: 2 }}>
                {[c.registered_address?.city || c.city, c.registered_address?.state || c.state].filter(Boolean).join(', ')}
              </div>
            </div>
            <span style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 11,
              color: 'hsl(0,0%,55%)' }}>{c.code || '—'}</span>
            <span style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 11,
              color: 'hsl(0,0%,60%)' }}>{c.gstin || '—'}</span>
            <span style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 11,
              color: 'hsl(0,0%,70%)' }}>{c.contact_name || '—'}</span>
            <span style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 10,
              color: 'hsl(0,0%,55%)' }}>{c.transporter_id || '—'}</span>
            <span style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 10,
              color: c.gta_gst_type === 'reverse_5' ? 'hsl(197,80%,55%)' : 'hsl(43,80%,55%)' }}>
              {c.gta_gst_type ? gtaLabel(c.gta_gst_type) : '—'}
            </span>
            <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
              <button onClick={() => { setEditCarrier(c); setShowModal(true); }}
                data-testid="edit-carrier-btn"
                style={{ background: 'none', border: 'none', cursor: 'pointer',
                  color: 'hsl(0,0%,50%)', padding: 0 }}>
                <PencilSimple size={13} />
              </button>
              <button onClick={() => toggleStatus(c)}
                data-testid="toggle-carrier-status-btn"
                style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 9,
                  padding: '2px 8px', cursor: 'pointer',
                  background: c.status === 'active' ? 'hsl(0,0%,12%)' : 'hsl(0,0%,10%)',
                  border: `1px solid ${c.status === 'active' ? 'hsl(142,50%,35%)' : 'hsl(0,0%,25%)'}`,
                  color: c.status === 'active' ? 'hsl(142,60%,50%)' : 'hsl(0,0%,45%)' }}>
                {c.status === 'active' ? 'ACTIVE' : 'INACTIVE'}
              </button>
            </div>
          </div>
        ))}
      </div>

      {showModal && (
        <CarrierModal
          carrier={editCarrier}
          onClose={() => setShowModal(false)}
          onSaved={() => { setShowModal(false); load(); }}
        />
      )}
    </div>
  );
}
